import { useCallback } from 'react'
import LazyLoad from 'react-lazyload'

import { embeddedEntryComponentsMap } from '../constants/embeddedEntryComponentsMap'
import Img from '../../Img'
import { PdfViewerClient } from '../../PdfViewer/PdfViewerClient'
import { CONTENT_TYPES } from 'src/common/constants'
import { useAnalytics } from 'src/common/analytics'
import { useArticle } from '../../ArticleProvider'

export const EmbeddedAsset = ({ node }) => {
  const { track } = useAnalytics()
  const source = node?.data?.target?.fields

  if (source?.file?.contentType === 'application/pdf') {
    const handleTrackEvent = ({ click_id, click_text }) => {
      track(
        { click_id, click_text, nav_link_section: 'PDF Viewer' },
        {},
        'click'
      )
    }

    return (
      <LazyLoad height={400} offset={200} once>
        <div className="my-8" data-testid="embedded-asset-pdf">
          <PdfViewerClient
            src={source.file.url.replace(/^\/\//, 'https://')}
            onTrackEvent={handleTrackEvent}
          />
        </div>
      </LazyLoad>
    )
  }

  if (source?.file?.contentType?.includes('image')) {
    return (
      <div className="mx-auto my-8 text-center" data-testid="embedded-asset">
        <Img
          src={source?.file?.url.replace(/^\/\//, 'https://')}
          layout="intrinsic"
          height={source.file.details.image.height}
          width={source.file.details.image.width}
          alt={source?.title}
          styles={{ position: 'relative' }}
        />
        {!!source?.description && (
          <figcaption className="mt-8 text-center text-ms text-gray-240">
            <em>{source.description}</em>
          </figcaption>
        )}
      </div>
    )
  }

  return null
}

export const EmbeddedEntry = ({ node }) => {
  const { track } = useAnalytics()
  const { articleTitle } = useArticle()

  const onPlay = useCallback(() => {
    track(
      {
        click_id: 'Video Player Play Initiated',
        page_name: `FDR-Web | ${articleTitle}`,
        page_path: `FDR-Web | ${document.location.pathname?.slice(1)}`,
        page_url: document.location.href,
      },
      {},
      'click'
    )
  }, [articleTitle, track])

  const target = node?.data?.target
  const contentTypeId = target?.sys?.contentType?.sys?.id

  if (!target?.fields || !contentTypeId) {
    return null
  }

  const { fields, sys } = target
  const { id } = sys.contentType.sys

  const Component = embeddedEntryComponentsMap[id]
  if (Component) {
    if (id === CONTENT_TYPES.YOUTUBE_VIDEO) {
      const {
        aspectRatio,
        autoplay,
        name,
        url,
        thumbnailImage,
        ...restFields
      } = fields
      const imageFields = thumbnailImage?.fields ?? {}

      return (
        <Component
          videoUrl={url}
          aspectRatio={aspectRatio}
          autoPlay={autoplay}
          title={name}
          thumbnailUrl={
            imageFields?.file?.url ? `https:${imageFields?.file?.url}` : ''
          }
          onPlay={onPlay}
          {...restFields}
        />
      )
    }

    return <Component {...fields} />
  }

  return <div />
}
